import React, { useEffect, useState } from 'react';
import {
  Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  MenuItem, Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { Plus, Upload } from 'lucide-react';
import {
  branchOwnedAccounts, branchCreateOwnedAccount, branchUpdateOwnedAccount,
  branchSetOwnedAccountStatus, branchDeleteOwnedAccount,
} from '../../services/branchApi';

const METHODS = ['UPI', 'IMPS', 'NEFT', 'BANK_TRANSFER', 'CRYPTO'];
const MODES = ['MANUAL', 'AUTO'];
const STATUS_COLOR: any = { active: 'success', paused: 'warning', suspended: 'error' };
const money = (v: any) => `₹${Number(v || 0).toLocaleString()}`;
const empty = { label: '', method: 'UPI', mode: 'MANUAL', accountHolderName: '', accountNumber: '', ifscCode: '', upiId: '', bankName: '', minAmount: 0, maxAmount: 0, dailyLimit: 0, status: 'active' };

export default function BranchAccounts() {
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [dlg, setDlg] = useState<null | { id?: number }>(null);
  const [form, setForm] = useState<any>(empty);
  const [qr, setQr] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const load = async () => { setLoading(true); try { setRows((await branchOwnedAccounts()).data || []); } finally { setLoading(false); } };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setForm(empty); setQr(null); setError(''); setDlg({}); };
  const openEdit = (r: any) => {
    setForm({
      label: r.label, method: r.method, mode: r.mode, accountHolderName: r.accountHolderName, accountNumber: r.accountNumber,
      ifscCode: r.ifscCode, upiId: r.upiId, bankName: r.bankName, minAmount: r.minAmount, maxAmount: r.maxAmount,
      dailyLimit: r.dailyLimit, status: r.status,
    });
    setQr(null); setError(''); setDlg({ id: r.id });
  };
  const set = (k: string, v: any) => setForm((f: any) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true); setError('');
    try {
      if (dlg!.id) await branchUpdateOwnedAccount(dlg!.id, form, qr);
      else await branchCreateOwnedAccount(form, qr);
      setDlg(null); await load();
    } catch (e: any) { setError(e.message); } finally { setSaving(false); }
  };

  const cycleStatus = async (r: any) => {
    const next = r.status === 'active' ? 'paused' : 'active';
    await branchSetOwnedAccountStatus(r.id, next); await load();
  };
  const remove = async (r: any) => { if (window.confirm(`Delete account "${r.label}"?`)) { await branchDeleteOwnedAccount(r.id); await load(); } };

  const isUpi = form.method === 'UPI';

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Deposit / Withdraw Accounts</Typography>
          <Typography variant="caption" color="text.secondary">Your branch's own bank accounts. Requests in each account's amount range route to it.</Typography>
        </Box>
        <Button variant="contained" startIcon={<Plus size={16} />} onClick={openCreate}>Add Account</Button>
      </Stack>

      {loading ? <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box> : (
        <Table size="small">
          <TableHead>
            <TableRow>
              {['Label', 'Method', 'Mode', 'Account / UPI', 'Range', 'Daily limit', 'Status', 'Actions'].map((h) => (
                <TableCell key={h} sx={{ color: 'text.secondary' }}>{h}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.id} hover>
                <TableCell>{r.label || '-'}</TableCell>
                <TableCell>{r.method}</TableCell>
                <TableCell>{r.mode}</TableCell>
                <TableCell sx={{ fontSize: 12 }}>{r.upiId || `${r.bankName || ''} ${r.accountNumber || ''}`.trim() || '-'}</TableCell>
                <TableCell sx={{ fontSize: 12 }}>{money(r.minAmount)} – {r.maxAmount ? money(r.maxAmount) : '∞'}</TableCell>
                <TableCell sx={{ fontSize: 12 }}>{r.dailyLimit ? money(r.dailyLimit) : '∞'}</TableCell>
                <TableCell>
                  <Chip size="small" color={STATUS_COLOR[r.status]} label={r.status} onClick={() => cycleStatus(r)} sx={{ cursor: 'pointer' }} />
                </TableCell>
                <TableCell>
                  <Stack direction="row" spacing={1}>
                    <Button size="small" onClick={() => openEdit(r)}>Edit</Button>
                    <Button size="small" color="error" onClick={() => remove(r)}>Delete</Button>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
            {!rows.length && <TableRow><TableCell colSpan={8} align="center" sx={{ py: 3, color: 'text.secondary' }}>No accounts yet.</TableCell></TableRow>}
          </TableBody>
        </Table>
      )}

      <Dialog open={!!dlg} onClose={() => setDlg(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{dlg?.id ? 'Edit Account' : 'Add Account'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Label" size="small" value={form.label} onChange={(e) => set('label', e.target.value)} />
            <Stack direction="row" spacing={1}>
              <TextField select label="Method" size="small" fullWidth value={form.method} onChange={(e) => set('method', e.target.value)}>
                {METHODS.map((m) => <MenuItem key={m} value={m}>{m}</MenuItem>)}
              </TextField>
              <TextField select label="Mode" size="small" fullWidth value={form.mode} onChange={(e) => set('mode', e.target.value)}>
                {MODES.map((m) => <MenuItem key={m} value={m}>{m === 'MANUAL' ? 'Manual' : 'Automatic'}</MenuItem>)}
              </TextField>
            </Stack>
            {isUpi
              ? <TextField label="UPI ID" size="small" value={form.upiId} onChange={(e) => set('upiId', e.target.value)} />
              : <>
                  <TextField label="Account holder" size="small" value={form.accountHolderName} onChange={(e) => set('accountHolderName', e.target.value)} />
                  <Stack direction="row" spacing={1}>
                    <TextField label="Account number" size="small" fullWidth value={form.accountNumber} onChange={(e) => set('accountNumber', e.target.value)} />
                    <TextField label="IFSC" size="small" fullWidth value={form.ifscCode} onChange={(e) => set('ifscCode', e.target.value)} />
                  </Stack>
                  <TextField label="Bank name" size="small" value={form.bankName} onChange={(e) => set('bankName', e.target.value)} />
                </>}
            <Stack direction="row" spacing={1}>
              <TextField label="Min amount" type="number" size="small" fullWidth value={form.minAmount} onChange={(e) => set('minAmount', Number(e.target.value))} />
              <TextField label="Max amount (0 = ∞)" type="number" size="small" fullWidth value={form.maxAmount} onChange={(e) => set('maxAmount', Number(e.target.value))} />
              <TextField label="Daily limit (0 = ∞)" type="number" size="small" fullWidth value={form.dailyLimit} onChange={(e) => set('dailyLimit', Number(e.target.value))} />
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center">
              <TextField select label="Status" size="small" sx={{ minWidth: 140 }} value={form.status} onChange={(e) => set('status', e.target.value)}>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="paused">Paused</MenuItem>
              </TextField>
              <Button component="label" variant="outlined" startIcon={<Upload size={16} />}>
                {qr ? qr.name : 'QR image (optional)'}
                <input hidden type="file" accept="image/*" onChange={(e) => setQr(e.target.files?.[0] || null)} />
              </Button>
            </Stack>
            {error && <Typography color="error" variant="body2">{error}</Typography>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDlg(null)}>Cancel</Button>
          <Button variant="contained" onClick={save} disabled={saving || !form.method}>{saving ? 'Saving…' : 'Save'}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
